import Icon from '../icons/Icon'
import MuiPagination from '@mui/material/Pagination'
import MuiPaginationItem, { PaginationItemProps as MuiPaginationItemProps } from '@mui/material/PaginationItem'
import Stack, { StackProps } from '@mui/material/Stack'
import { useRouter } from 'next/router'
import NextLink from 'next/link'
import { useTranslation } from 'next-i18next'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight'
import { getPageHref } from '@/helpers/filters'
import { PagingMetadata } from '@/interfaces/projects'
import { isNil } from '@/helpers/helpers'

interface PaginationProps extends StackProps {
  pagingMetadata: PagingMetadata
}

interface PaginationItemProps extends MuiPaginationItemProps<'a'> {
  page: React.ReactNode | number
  nextPage?: string
  hidePageNumbers?: boolean
}

const PaginationItem = ({ page, nextPage, hidePageNumbers, ...props }: PaginationItemProps): React.ReactElement => {
  const { asPath } = useRouter()
  const { t } = useTranslation('projects')

  if (props.type === 'page' && hidePageNumbers) {
    return null
  }

  if (props.type === 'next' && !isNil(nextPage)) {
    page = Number(nextPage)
    props.disabled = false
  }

  const href = !props.disabled ? getPageHref(page as number, asPath) : null

  if (hidePageNumbers) {
    if (props.type === 'next') {
      page = (
        <>
          {t('next')} <Icon icon={faChevronRight} style={{ marginLeft: 5 }} />
        </>
      )
    } else if (props.type === 'previous') {
      page = (
        <>
          <Icon icon={faChevronLeft} style={{ marginRight: 5 }} /> {t('previous')}
        </>
      )
    }
    props.type = 'page'
  }

  if (page === null || href === null) {
    return <MuiPaginationItem component="a" {...props} page={page} />
  }
  return (
    <NextLink href={href} passHref legacyBehavior prefetch={false}>
      <MuiPaginationItem component="a" {...props} page={page} />
    </NextLink>
  )
}

export const Pagination = ({ pagingMetadata, ...props }: PaginationProps): React.ReactElement => {
  return (
    <Stack direction="row" justifyContent="center" {...props}>
      <MuiPagination
        count={pagingMetadata.totalPages}
        page={pagingMetadata.page}
        shape="rounded"
        color="primary"
        renderItem={(item): React.ReactElement => (
          <PaginationItem
            {...item}
            nextPage={pagingMetadata.nextPage}
            hidePageNumbers={isNil(pagingMetadata.totalPages)}
          />
        )}
      />
    </Stack>
  )
}
